/** Compact labels used in tight surfaces (calendar rail, property badges,
 *  task lists). Properties carry an admin-supplied `shortCode`; when absent
 *  we derive one from the name as a *suggestion*, which the user overrides
 *  from the form. No stop-words or language-specific rules: those heuristics
 *  are wrong as often as they're right.
 */

import { EMPTY_VALUE } from "@/lib/empty-value";

export const PROPERTY_SHORT_CODE_MAX_LENGTH = 4;
export const UNIT_CODE_MAX_LENGTH = 20;

const WHITESPACE_RE = /\s+/u;
const NOT_ALPHANUMERIC_RE = /[^\p{L}\p{N}]/gu;
const DIGIT_RE = /\p{N}/u;

/** Drop combining marks that follow Latin letters (e.g. "Hôtel" →
 *  "Hotel"). Marks in non-Latin scripts (Arabic, Devanagari, Thai, …)
 *  carry phonetic information that's *not* visual noise, so we leave
 *  those alone. */
function stripLatinDiacritics(s: string): string {
  return s
    .normalize("NFD")
    .replace(/(?<letter>\p{Script=Latin})\p{M}+/gu, "$<letter>")
    .normalize("NFC");
}

/** First `n` code points of `s`, so an astral character is never split. */
function head(s: string, n: number): string {
  return Array.from(s).slice(0, n).join("");
}

/** Suggest a code from a name: a token holding a digit is kept whole (a room
 *  number), every other word gives its initial ("Room 12" → "R12", "101" →
 *  "101"); a single word gives its first two letters. A piece that does not
 *  fit whole gives only its initial letter, so a number is never cut. Empty
 *  only for a name with no letter or digit. */
export function deriveShortCodeFromName(
  name: string,
  maxLength: number
): string {
  const tokens = stripLatinDiacritics(name)
    .split(WHITESPACE_RE)
    .map((t) => t.replace(NOT_ALPHANUMERIC_RE, ""))
    .filter((t) => t.length > 0);
  const [first] = tokens;
  if (first === undefined) {
    return "";
  }
  const pieces =
    tokens.length === 1 && !DIGIT_RE.test(first)
      ? [head(first, 2)]
      : tokens.map((t) => (DIGIT_RE.test(t) ? t : head(t, 1)));
  let code = "";
  for (const piece of pieces) {
    const whole = piece.toUpperCase();
    const initial = head(whole, 1);
    if (Array.from(code + whole).length <= maxLength) {
      code += whole;
    } else if (!DIGIT_RE.test(initial) && Array.from(code).length < maxLength) {
      code += initial;
    }
  }
  return code;
}

/** First free `<base><suffix>[-n]` code among a property's units: codes are
 *  unique per property (`units_property_code_uq`). The base is cut so the
 *  whole code fits the limit. */
export function nextFreeUnitCode(
  taken: Set<string>,
  base: string,
  suffix = ""
): string {
  for (let n = 1; ; n += 1) {
    const tail = n === 1 ? suffix : `${suffix}-${n}`;
    const candidate = `${head(base, UNIT_CODE_MAX_LENGTH - Array.from(tail).length)}${tail}`;
    if (!taken.has(candidate)) {
      return candidate;
    }
  }
}

/** Compact label for the calendar rail / property badges. Admin-supplied
 *  `shortCode` wins; otherwise derive from the name. */
export function propertyShortLabel(property: {
  name: string;
  shortCode?: string | null;
}): string {
  return (
    property.shortCode?.trim() ||
    deriveShortCodeFromName(property.name, PROPERTY_SHORT_CODE_MAX_LENGTH) ||
    EMPTY_VALUE
  );
}
